import { useEffect, useMemo, useState } from "react";
import { view } from "@risingstack/react-easy-state";
import GenericPageContainer from "../../../GenericPageElements/GenericPageContainer";
import request from "../../../helpers/request";
import endpoints from "../../../helpers/endpoints";
import "./ServiceComponents.scss";
import { useTranslations } from "../../../helpers/hooks";

const tableHeaders = [
    'water',
    'sewerage',
    'landlord_spid',
    'pairing_reference_code',
    'uprn',
    'voa',
    'supply_point_address',
    'customer_name',
    'occupancy',
    "water_tariff",
    "sewerage_tariff",
    "sw_tariff",
    "hd_tariff",
    'assigned_to_name',
    'spid',
]

const potentialGapHeaders = [
    'ba_reference',
    'number_name',
    'street',
    'town',
    'postal_district',
    'county',
    'postcode',
    'spid',
]

const sortableHeaders = [
    'landlord_spid',
    'pairing_reference_code',
    'uprn',
    'voa',
    'supply_point_address',
    'customer_name',
]

const potentialGapSortableHeaders = [
    'ba_reference',
    'number_name',
    'street',
    'town',
    'postal_district',
    'county',
    'postcode',
]

const statusOptions = [
    "Consolidate - Same Address",
    "Consolidate - Similar Address",
    "Consolidate - Address Variation",
    "Consolidate - Multi Meter",
    "Gap Site",
    "Landlord SPID Pairing",
    "Pair - Same Address",
    "Pair - Similar Address",
    "Pair - Address Variation",
    "Escalate",
    "Potential Missing SWHD",
    "No Action Required",
    "Unable to Match",
]

const sortValues = (left, right, direction) => {
    const leftNumber = parseFloat(left);
    const rightNumber = parseFloat(right);
    const leftIsNumber = !Number.isNaN(leftNumber) && left !== '';
    const rightIsNumber = !Number.isNaN(rightNumber) && right !== '';

    if (leftIsNumber && rightIsNumber) {
        if (leftNumber < rightNumber) return direction === 'asc' ? -1 : 1;
        if (leftNumber > rightNumber) return direction === 'asc' ? 1 : -1;
        return 0;
    }

    if (left < right) return direction === 'asc' ? -1 : 1;
    if (left > right) return direction === 'asc' ? 1 : -1;
    return 0;
}

const sortRowsByColumn = (rows, sortBy, getValue) => {
    return [...rows].sort((leftRow, rightRow) => {
        const leftValue = getValue(leftRow, sortBy.column) || '';
        const rightValue = getValue(rightRow, sortBy.column) || '';

        return sortValues(leftValue, rightValue, sortBy.direction);
    })
}

const TableStateRows = ({ isLoading, columnCount }) => {
    if (isLoading) {
        return (
            <>
                <tr aria-hidden="true" style={{ height: 0, visibility: 'collapse' }}>
                    {new Array(columnCount).fill('').map(() => <td></td>)}
                </tr>
                <tr>
                    <td colSpan={columnCount}>
                        <i className="fa fa-spinner fa-spin"></i>
                    </td>
                </tr>
            </>
        )
    }

    return (
        <tr>
            {new Array(columnCount).fill('').map(() => <td></td>)}
        </tr>
    )
}

export default view(function ServiceComponents() {
    const [search, setSearch] = useState("");
    const [partialPostcodeSearch, setPartialPostcodeSearch] = useState("");
    const [currentPostcode, setCurrentPostcode] = useState("")
    const [results, setResults] = useState([])
    const [partialResults, setPartialResults] = useState([])
    const [potentialGaps, setPotentialGaps] = useState([])
    const [errors, setErrors] = useState([])
    const [partialErrors, setPartialErrors] = useState([])
    const [loading, setLoading] = useState(false)
    const [loadingPartial, setLoadingPartial] = useState(false)
    const [loadingWorkflow, setLoadingWorkflow] = useState(false)
    const [sortBy, setSortBy] = useState({ column: 'uprn', direction: 'asc' })
    const [partialSortBy, setPartialSortBy] = useState({ column: 'uprn', direction: 'asc' })
    const [potentialGapsSortBy, setPotentialGapsSortBy] = useState({ column: 'ba_reference', direction: 'asc' })
    const [workflowItems, setWorkflowItems] = useState([])

    var _cur = useMemo(() => {
        return workflowItems.findIndex(_ => _ === search);
    }, [workflowItems, search]);

    const handleRequestError = (setErrorState, message, { clearPotentialGaps = false } = {}) => (error) => {
        console.log(error)
        setErrorState([message])
        if (clearPotentialGaps) {
            setPotentialGaps([])
        }
        setLoading(false)
        setLoadingPartial(false)
    }

    const getWorkflowItems = () => {
        setLoadingWorkflow(true)
        request(true).get(endpoints.SERVICE_COMPONENTS_WORKFLOW_ITEMS).then(r => {
            setWorkflowItems(r.data)
            if (r.data.length > 0) {
                const match = r.data[_cur && _cur !== -1 ? _cur : 0] ?? r.data[_cur - 1] ?? r.data[0]
                setSearch(match)
                setCurrentPostcode(match)
                handleSearch({ showSpinner: true, postcode: match})
            }
        }).catch(error => {
            console.log(error)
        }).finally(() => {
            setLoadingWorkflow(false)
        })
    }

    const getWorkflowNext = () => {
        if (_cur !== workflowItems.length-1) {
            const match = workflowItems[_cur+1]
            setSearch(match);
            setCurrentPostcode(match)
            handleSearch({ showSpinner: true, postcode: match})
        }
    }

    const getWorkflowLast = () => {
        if (_cur !== 0) {
            const match = workflowItems[_cur-1]
            setSearch(match);
            setCurrentPostcode(match)
            handleSearch({ showSpinner: true, postcode: match})
        }
    }

    const handleSearch = ({ showSpinner = true, postcode } = {}) => {
        setErrors([]);
        if (showSpinner) {
            setLoading(true)
        }

        const searchPostcode = postcode ?? search;

        request(true).get(endpoints.SERVICE_COMPONENTS_SEARCH, {
            doesCancel: true,
            params: {
                postcode: searchPostcode
            }
        }).then(async response => {
            setLoading(false)
            if (!response.data.rows || response.data.rows.length === 0) {
                setErrors(["No results for that search"])
                setResults([])
                setPotentialGaps([])
                return
            }

            setCurrentPostcode(searchPostcode)
            setResults(response.data.rows)
            setPotentialGaps(response.data.potential_gaps || [])
        }).catch(handleRequestError(setErrors, "Error retrieving data", { clearPotentialGaps: true }))
    }

    const handlePartialPostcodeSearch = ({ showSpinner = true, postcode } = {}) => {
        setPartialErrors([]);
        const searchPostcode = postcode ?? partialPostcodeSearch;
        const trimmedSearchPostcode = searchPostcode.trim();
        const isTooShort = trimmedSearchPostcode.length < 2;

        if (isTooShort) {
            setPartialResults([])
            setPartialErrors(["Enter at least two characters in the postcode"])
            return
        }

        if (showSpinner) {
            setLoadingPartial(true)
        }

        request(true).get(endpoints.SERVICE_COMPONENTS_POSTCODE_SEARCH, {
            doesCancel: true,
            params: {
                postcode: trimmedSearchPostcode
            }
        }).then(response => {
            setLoadingPartial(false)
            if (!response.data.rows || response.data.rows.length === 0) {
                setPartialErrors(["No results for that search"])
                setPartialResults([])
                return
            }

            setPartialResults(response.data.rows)
        }).catch(handleRequestError(setPartialErrors, "Error retrieving data"))
    }

    /**
     * @param {object} data 
     * @param {string} data.core_spid
     * @param {?string} data.spid
     * @param {?string} data.status
     */
    const refreshSearchResults = () => {
        const hasSearch = !!search.trim();
        const hasCurrentPostcode = !!currentPostcode.trim();
        const searchPostcode = hasCurrentPostcode ? currentPostcode : search;

        if (hasSearch || hasCurrentPostcode) {
            handleSearch({ showSpinner: false, postcode: searchPostcode });
        }

        if (partialPostcodeSearch.trim()) {
            handlePartialPostcodeSearch({ showSpinner: false, postcode: partialPostcodeSearch });
        }
    }

    const submitAndRefresh = (endpoint, data) => {
        return request(true).post(endpoint, data)
            .then(() => {
                refreshSearchResults()
            }).catch(handleRequestError(setErrors, "Error updating data"))
    }

    const handleUpdate = (data) => {
        return submitAndRefresh(endpoints.SERVICE_COMPONENTS_UPDATE, data)
    }

    /**
     * @param {string} column
     * @param {string[]} supportedHeaders
     * @param {(updater: (prev: { column: string, direction: 'asc' | 'desc' }) => { column: string, direction: 'asc' | 'desc' }) => void} setSortState
     */
    const handleUpdateSortBy = (column, supportedHeaders, setSortState) => {
        if (!supportedHeaders.includes(column)) {
            return
        }

        setSortState(prev => {
            if (prev.column === column) {
                return {
                    column,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc'
                }
            }

            return { column, direction: 'asc' }
        })
    }

    const excludePostcode = () => {
        const postcodeToExclude = currentPostcode.trim() || search.trim();

        if (window.confirm("Are you sure you wish to mark this postcode as complete?")) {
            request(true).post(endpoints.SERVICE_COMPONENTS_EXCLUDE, {
                postcode: postcodeToExclude
            }).then(() => {
                setCurrentPostcode("")
                getWorkflowItems();
            })
        }
    }

    useEffect(() => {
        getWorkflowItems();
    }, [])

    return (
        <GenericPageContainer
            title="Service Components"
            titleIconClass={"fak fa-id-business-icon fa-2x colour-primary"}
            containerWidth="95vw"
        >
            <div className="grid grid-gap-15 service-components">
                <div className="grid grid-columns-2 grid-gap-15" style={{ gridTemplateColumns: 'repeat(4, min-content)' }}>
                    <SearchInput search={search} setSearch={setSearch} handleSearch={handleSearch} loading={loading} />
                    <button disabled={loading || loadingWorkflow} style={{ width: '10em' }} className="button compact smaller-text background-primary colour-white" onClick={handleSearch}>
                        {
                            loading || loadingWorkflow ?
                                <i className="fa fa-spinner fa-spin"></i>
                                :
                                "Search"
                        }
                    </button>
                    <div className="workflow-container">
                        <span className='fas fa-angle-left fa-xl colour-primary' style={{cursor: 'pointer'}} onClick={getWorkflowLast}></span>
                        <span>{_cur + 1}/{workflowItems.length}</span>
                        <span className='fas fa-angle-right fa-xl colour-primary' style={{cursor: 'pointer'}} onClick={getWorkflowNext}></span>
                    </div>
                    <div>
                        <button
                            disabled={loading || loadingWorkflow || results.length === 0}
                            className="button compact smaller-text background-primary colour-white"
                            style={{ width: 'max-content' }}
                            onClick={excludePostcode}
                        >
                            Complete Postcode
                        </button>
                    </div>
                </div>
                {errors.map(error => { return <p style={{ color: "var(--red)" }}>{error}</p> })}
                <div className="service-components-table-container">
                    <Table 
                        isLoading={loading || loadingWorkflow} 
                        results={results} 
                        rowStateKey={currentPostcode || search}
                        tableHeaders={tableHeaders} 
                        onUpdateStatus={(status, coreSpid, extraData = {}) => handleUpdate({ status, core_spid: coreSpid, ...extraData })} 
                        onUpdateSPID={(spid, coreSpid, extraData = {}) => handleUpdate({ spid, core_spid: coreSpid, ...extraData })} 
                        sortBy={sortBy}
                        updateSortBy={(column) => handleUpdateSortBy(column, sortableHeaders, setSortBy)}
                    />
                </div>
                <h3>Postcode Search</h3>
                <div className="grid grid-columns-2 grid-gap-15" style={{ gridTemplateColumns: 'repeat(2, min-content)' }}>
                    <SearchInput
                        search={partialPostcodeSearch}
                        setSearch={setPartialPostcodeSearch}
                        handleSearch={handlePartialPostcodeSearch}
                        loading={loadingPartial}
                        placeholder="Enter partial postcode"
                    />
                    <button
                        disabled={loadingPartial}
                        style={{ width: '14em' }}
                        className="button compact smaller-text background-primary colour-white"
                        onClick={handlePartialPostcodeSearch}
                    >
                        {
                            loadingPartial ?
                                <i className="fa fa-spinner fa-spin"></i>
                                :
                                "Partial Postcode Search"
                        }
                    </button>
                </div>
                {partialErrors.map(error => { return <p style={{ color: "var(--red)" }}>{error}</p> })}
                <div className="service-components-table-container">
                    <Table
                        isLoading={loadingPartial}
                        results={partialResults}
                        rowStateKey={partialPostcodeSearch}
                        tableHeaders={tableHeaders}
                        onUpdateStatus={(status, coreSpid, extraData = {}) => handleUpdate({ status, core_spid: coreSpid, ...extraData })}
                        onUpdateSPID={(spid, coreSpid, extraData = {}) => handleUpdate({ spid, core_spid: coreSpid, ...extraData })}
                        sortBy={partialSortBy}
                        updateSortBy={(column) => handleUpdateSortBy(column, sortableHeaders, setPartialSortBy)}
                    />
                </div>
                <h3>SWHD-VOA</h3>
                <div className="service-components-table-container">
                    <PotentialGapsTable
                        isLoading={loading}
                        results={potentialGaps}
                        headers={potentialGapHeaders}
                        rowStateKey={currentPostcode || search}
                        sortBy={potentialGapsSortBy}
                        updateSortBy={(column) => handleUpdateSortBy(column, potentialGapSortableHeaders, setPotentialGapsSortBy)}
                        onUpdatePotentialGap={(data) => handleUpdate(data)}
                    />
                </div>
            </div>
        </GenericPageContainer>
    );
}) 

const SearchInput = ({ search, setSearch, handleSearch, loading, placeholder = "Enter postcode" }) => {
    return (
        <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value.toUpperCase())}
            placeholder={placeholder}
            className="data-row-field"
            disabled={loading}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    handleSearch();
                }
            }}
        />
    )
}

/**
 * @param {object} props 
 * @param {boolean} props.isLoading
 * @param {object} props.results
 * @param {string[]} props.tableHeaders
 * @param {string} props.rowStateKey
 * @param {(status: string, coreSpid: string) => void} props.onUpdateStatus
 * @param {(spid: string, coreSpid: string) => void} props.onUpdateSPID
 * @param {{ column: string, direction: 'asc' | 'desc' }} props.sortBy
 * @param {(column: string) => void} props.updateSortBy
 */
const Table = ({ 
    isLoading, 
    results, 
    rowStateKey,
    tableHeaders, 
    onUpdateStatus,
    onUpdateSPID,
    sortBy,
    updateSortBy, 
}) => {
    const translations = useTranslations('service_components')

    const sortedResults = useMemo(() => {
        return sortRowsByColumn(results, sortBy, (row, column) => row.data[column])
    }, [results, sortBy])

    return (
        <table className="table borders smaller-text">
            <thead>
                <tr>
                    {
                        tableHeaders.map(header => 
                            <th onClick={() => updateSortBy(header)} style={{ cursor: sortableHeaders.includes(header) ? 'pointer' : 'default' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5em' }}>
                                    <span>{translations[header]}</span>
                                    {sortBy.column === header ? (
                                        <span>
                                            <i className={`fa fa-sort-${sortBy.direction === 'asc' ? 'up' : 'down'}`}></i>
                                        </span>
                                    ) : ''}
                                </div>
                            </th>
                        )
                    }
                </tr>
            </thead>
        <tbody>
            {
                results.length > 0 && !isLoading ?
                    Object.values(sortedResults).map(result => 
                        <TableRow 
                            key={`${rowStateKey}-${result.core_spid || `${result.data.voa}-${result.postcode}`}`}
                            result={result} 
                            onUpdateStatus={(status, extraData) => onUpdateStatus(status, result.core_spid, {
                                voa: result.data.voa,
                                postcode: result.postcode,
                                ...extraData,
                            })} 
                            onUpdateSPID={(spid, extraData) => onUpdateSPID(spid, result.core_spid, {
                                voa: result.data.voa,
                                postcode: result.postcode,
                                ...extraData,
                            })} 
                        />
                    )
                :
                <TableStateRows isLoading={isLoading} columnCount={tableHeaders.length} />
            }
        </tbody>
        </table>
    )
}

/**
 * @param {object} props 
 * @param {object} props.result
 * @param {(status: string) => void} props.onUpdateStatus
 * @param {(spid: string) => void} props.onUpdateSPID
 */
const TableRow = ({ result, onUpdateStatus, onUpdateSPID }) => {
    const [spid, setSpid] = useState(result.spid || '')
    const [landlordSpid, setLandlordSpid] = useState(result.data.landlord_spid || '')
    const [status, setStatus] = useState(result.status || '');

    useEffect(() => {
        setStatus(result.status || '');
    }, [result.status]);

    useEffect(() => {
        setSpid(result.spid || '');
    }, [result.spid]);

    useEffect(() => {
        setLandlordSpid(result.data.landlord_spid || '');
    }, [result.data.landlord_spid]);

    return (
        <tr>
            {
                tableHeaders.filter(header => header !== 'spid').map(header => {
                    return (
                        <td style={{ borderBottom: '1px solid black' }}>
                            {
                                typeof result.data[header] === 'object' && result.data[header] ?
                                    Object.values(result.data[header]).map(value => {
                                        return (
                                            <div>{value}</div>
                                        )
                                    })
                                :
                                    result.data[header]
                            }
                        </td>
                    )
                })
            }
            <td style={{ borderBottom: '1px solid black' }}>
                <TableRowActions
                    landlordSpid={landlordSpid}
                    setLandlordSpid={setLandlordSpid}
                    onUpdateSPID={onUpdateSPID}
                    onUpdateStatus={onUpdateStatus}
                    setStatus={setStatus}
                    spid={spid}
                    setSpid={setSpid}
                    status={status}
                />
            </td>
        </tr>
    )
}

const PotentialGapsTable = ({ isLoading, results, headers, rowStateKey, sortBy, updateSortBy, onUpdatePotentialGap }) => {
    const translations = useTranslations('service_components')

    const sortedResults = useMemo(() => {
        return sortRowsByColumn(results, sortBy, (row, column) => row[column])
    }, [results, sortBy])

    const handleUpdatePotentialGapSortBy = (column) => {
        if (!potentialGapSortableHeaders.includes(column)) {
            return
        }

        updateSortBy(column)
    }

    return (
        <table className="table borders smaller-text">
            <thead>
                <tr>
                    {
                        headers.map(header =>
                            <th onClick={() => handleUpdatePotentialGapSortBy(header)} style={{ cursor: potentialGapSortableHeaders.includes(header) ? 'pointer' : 'default' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5em' }}>
                                    <span>{header === 'spid' ? 'Action' : translations[header]}</span>
                                    {sortBy.column === header ? (
                                        <span>
                                            <i className={`fa fa-sort-${sortBy.direction === 'asc' ? 'up' : 'down'}`}></i>
                                        </span>
                                    ) : ''}
                                </div>
                            </th>
                        )
                    }
                </tr>
            </thead>
            <tbody>
                {
                    results.length > 0 && !isLoading ?
                        sortedResults.map(result =>
                            <PotentialGapRow
                                key={`${rowStateKey}-${result.ba_reference}`}
                                result={result}
                                headers={headers}
                                onUpdatePotentialGap={onUpdatePotentialGap}
                            />
                        )
                    :
                    <TableStateRows isLoading={isLoading} columnCount={headers.length} />
                }
            </tbody>
        </table>
    )
}

const TableRowActions = ({
    landlordSpid,
    onUpdateSPID,
    onUpdateStatus,
    setLandlordSpid,
    setSpid,
    setStatus,
    spid,
    status,
}) => {
    const [isUpdating, setIsUpdating] = useState(false)
    const [hasUpdated, setHasUpdated] = useState(false)
    const hasSpid = !!spid.trim()
    const requiresLandlordSpid = status === 'Landlord SPID Pairing'
    const hasLandlordSpid = !!landlordSpid.trim()
    const isActionDisabled = isUpdating || !hasSpid || (requiresLandlordSpid && !hasLandlordSpid)

    const handleUpdateSpid = async () => {
        setIsUpdating(true)

        try {
            await onUpdateSPID(spid, {
                status,
                landlord_spid: status === 'Landlord SPID Pairing' ? landlordSpid : undefined,
            })
            setHasUpdated(true)
        } finally {
            setIsUpdating(false)
        }
    }

    return (
        <>
            <select 
                style={{ marginLeft: 0 }}
                value={status} 
                onChange={(e) => {
                    const nextStatus = e.target.value;
                    setStatus(nextStatus);

                    if (nextStatus !== 'Landlord SPID Pairing') {
                        onUpdateStatus(nextStatus, {
                            spid,
                        });
                    }
                }}
            >
                <option value=""></option>
                {
                    statusOptions.map(option => {
                        return (
                            <option value={option}>{option}</option>
                        )
                    })
                }
            </select>
            {
                status === 'Landlord SPID Pairing' ?
                    <input
                        type="text"
                        value={landlordSpid}
                        onChange={(e) => setLandlordSpid(e.target.value)}
                        placeholder="Landlord SPID"
                        style={{ boxSizing: 'border-box', marginTop: "0.5em" }}
                    />
                : null
            }
            <input
                type="text"
                value={spid}
                onChange={(e) => setSpid(e.target.value)}
                placeholder="Update SPID"
                style={{ boxSizing: 'border-box', marginTop: "0.5em" }}
            />
            <button 
                style={{
                    width: '100%',
                    marginTop: "0.5em",
                    marginInline: "auto",
                    opacity: isActionDisabled ? 0.6 : 1,
                    cursor: isActionDisabled ? 'default' : 'pointer',
                }}
                className="button compact smaller-text background-primary colour-white" 
                disabled={isActionDisabled}
                onClick={handleUpdateSpid}
            >
                <i className={`fa ${isUpdating ? 'fa-spinner fa-spin' : hasUpdated ? 'fa-xl fa-regular fa-circle-check' : 'fa-check'}`}></i>
            </button>
        </>
    )
}

const PotentialGapRow = ({ result, headers, onUpdatePotentialGap }) => {
    const [spid, setSpid] = useState('')
    const [landlordSpid, setLandlordSpid] = useState('')
    const [status, setStatus] = useState('')

    return (
        <tr>
            {
                headers.filter(header => header !== 'spid').map(header =>
                    <td style={{ borderBottom: '1px solid black' }}>
                        {typeof result[header] === 'boolean' ? (result[header] ? 'Yes' : 'No') : result[header]}
                    </td>
                )
            }
            <td style={{ borderBottom: '1px solid black', width: '1%', minWidth: '10rem' }}>
                <TableRowActions
                    landlordSpid={landlordSpid}
                    setLandlordSpid={setLandlordSpid}
                    onUpdateSPID={(nextSpid, extraData = {}) => onUpdatePotentialGap({
                        spid: nextSpid,
                        voa: result.ba_reference,
                        postcode: result.postcode,
                        ...extraData,
                    })}
                    onUpdateStatus={(nextStatus, extraData = {}) => onUpdatePotentialGap({
                        status: nextStatus,
                        voa: result.ba_reference,
                        postcode: result.postcode,
                        ...extraData,
                    })}
                    setStatus={setStatus}
                    spid={spid}
                    setSpid={setSpid}
                    status={status}
                />
            </td>
        </tr>
    )
}
